import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import useAppContext from 'flow/hooks/useAppContext';

import SettingRow from 'flow/components/Setting/SettingRow';
import Page from 'flow/components/Setting/Page';
import Toggle from 'flow/components/ui-components/Toggle';
import Divider from 'flow/components/ui-components/Divider';

import { CallMethodType, isRegisteredSIP } from 'flow/utils/call-utils';
import {
    setCallMethod,
    setAutoAnswerWithIpPhone,
} from 'flow/actions/app-actions';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import {
    getCallMethod,
    getAutoAnswerWithIpPhone,
    hasMobileExtension,
} from 'flow/selectors/selectors';

import { routes } from 'flow/types/routes';
import RadioButton from 'flow/components/ui-components/RadioButton';
import { generatePath } from 'flow/utils/router-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';

import useExtension from 'flow/hooks/useExtension';
import { Capabilities } from 'flow/utils/capability-utils';

const CallMethod = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { i18next } = useAppContext();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { extension } = useExtension(extensionKey);
    const hasCapabilitySoftphone = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SOFTPHONE),
    );
    const callMethod = useAppSelector(getCallMethod);
    const autoAnswerWithIpPhone = useAppSelector(getAutoAnswerWithIpPhone);
    const hasMex = useAppSelector(hasMobileExtension);
    const hasRegisteredTerminal = isRegisteredSIP(extension);

    const callMethodTypes = [
        CallMethodType.ALWAYS_ASK,
        CallMethodType.IP_PHONE,
    ];

    if (hasMex) {
        callMethodTypes.push(CallMethodType.MOBILE);
    }
    if (hasCapabilitySoftphone) {
        callMethodTypes.push(CallMethodType.LOCAL);
    }

    return (
        <Page
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone'], {
                        extensionKey,
                    }),
                )
            }
            header={'call.method'}
        >
            {callMethodTypes.map((option) => {
                const isSelected = callMethod.is(option);
                const showIpPhoneWarning =
                    option === CallMethodType.IP_PHONE &&
                    isSelected &&
                    !hasRegisteredTerminal;

                return (
                    <div key={`callMethodEnummItem-option-${option.value}`}>
                        <RadioButton
                            name={option.key}
                            checked={isSelected}
                            onChange={() => dispatch(setCallMethod(option))}
                        >
                            <>
                                {i18next.t(
                                    `settings.callmethod.${option.value}`,
                                )}
                                {showIpPhoneWarning ? (
                                    <div className="text-red">
                                        {i18next.t(
                                            'settings.callmethod.ipPhone.missing',
                                        )}
                                    </div>
                                ) : null}
                            </>
                        </RadioButton>
                    </div>
                );
            })}

            {callMethod === CallMethodType.IP_PHONE ? (
                <>
                    <Divider className="my-6" />
                    <SettingRow
                        cta={
                            <Toggle
                                value={autoAnswerWithIpPhone}
                                onChange={() =>
                                    dispatch(
                                        setAutoAnswerWithIpPhone(
                                            !autoAnswerWithIpPhone,
                                        ),
                                    )
                                }
                                text={i18next.t('call.method.auto.answer')}
                            />
                        }
                        information={i18next.t('call.method.hint.auto_answer')}
                    >
                        {i18next.t('call.method.auto.answer')}
                    </SettingRow>
                </>
            ) : null}
        </Page>
    );
};

export default CallMethod;
